import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MeApi, MonetisationApi } from 'src/api';
import { REVIEW_CLASSIC_BLOB_EXPERIENCE_TOKEN } from 'src/api/monetisation/freeTokenLimits';
import { useUserContext } from 'src/context/user';
import { useEligibility } from 'src/hooks/useEligibility';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';

/**
 * Meters classic review for free learners on the blob experience.
 * The upsell fires once `review_classic_blob_experience` is no longer available.
 * Learning new words stays open; words that are due for review do not.
 */
export const useReviewClassicBlobExperience = (active = true) => {
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const queryClient = useQueryClient();
  const consumedLearnableIds = useRef(new Set<number>());
  const [hasConsumedReviewedWord, setHasConsumedReviewedWord] = useState(false);
  const { eligibility, isLoading: isEligibilityLoading } = useEligibility(
    active ? languagePairId : null,
  );

  const isBlobExperience = !!eligibility?.eligibleForBlobExperience;
  const isFreeBlob = active && isBlobExperience && !!user && !user.isPro;

  const availabilityQuery = useQuery({
    queryKey: ['getFeaturesTokensAvailability', languagePairId],
    queryFn: () => MonetisationApi.getFeaturesTokensAvailability(languagePairId as number),
    enabled: isFreeBlob && !!languagePairId,
  });

  const reviewFeature = availabilityQuery.data?.find(
    feature => feature.type === REVIEW_CLASSIC_BLOB_EXPERIENCE_TOKEN,
  );
  const isExhausted = isFreeBlob && !!reviewFeature && !reviewFeature.available;

  const practiceQuery = useQuery({
    queryKey: ['practice', languagePairId],
    queryFn: () => MeApi.getPractice(languagePairId as number),
    enabled: isExhausted && !!languagePairId,
  });

  const consumeReviewedWord = useCallback(
    (learnableId: number) => {
      if (!isFreeBlob || !languagePairId || isExhausted) return;
      if (consumedLearnableIds.current.has(learnableId)) return;
      consumedLearnableIds.current.add(learnableId);
      setHasConsumedReviewedWord(true);

      void MonetisationApi.consumeFeatureToken(
        languagePairId,
        REVIEW_CLASSIC_BLOB_EXPERIENCE_TOKEN,
      ).finally(() => {
        void queryClient.invalidateQueries({
          queryKey: ['getFeaturesTokensAvailability', languagePairId],
        });
        void queryClient.invalidateQueries({ queryKey: ['practice', languagePairId] });
      });
    },
    [isExhausted, isFreeBlob, languagePairId, queryClient],
  );

  return {
    isBlobExperience: active && isBlobExperience,
    isFreeBlob,
    isLoading: isFreeBlob && (isEligibilityLoading || availabilityQuery.isPending),
    isExhausted,
    wordsLeftToReview: isExhausted ? (practiceQuery.data?.review.count ?? null) : null,
    isLoadingWordsLeftToReview: isExhausted && practiceQuery.isPending,
    /** True once this hook has spent a review token, i.e. the limit was hit mid-session. */
    hasConsumedReviewedWord,
    consumeReviewedWord,
  };
};
